<script lang="ts" setup>
import { i18n } from '@/i18n';
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import SvgLoader from '@/utils/SvgLoader.vue';
import { DownloadMenuItems, DOWNLOAD_MENU_ITEMS } from '../constants';

const props = defineProps<{
  isOpenProp?: boolean;
}>();

const t = i18n.t;

const isOpen = ref(false);

const OPTION_HEIGHT = 40;

const emit = defineEmits<{
  (e: 'click:selectInput'): void;
  (e: 'selectedItem', item: DownloadMenuItems): void;
  (e: 'close'): void;
}>();

watch(
  () => props.isOpenProp,
  () => {
    isOpen.value = props.isOpenProp || false;
  }
);

watch(isOpen, async (newVal) => {
  if (newVal) {
    emit('click:selectInput');
    calculateDropdownPosition();
  }
});

/**
 * メニュー項目の表示テキストを取得します。
 * @param item - メニュー項目
 */
const getDisplayText = (item: DownloadMenuItems): string => {
  switch (item) {
    case 'ForecastForReceiptFormat':
      return t('受領用内示フォーマット');
    case 'SalesResultAndCustomerForecast':
      return t('販売実績 & 客先内示');
    default:
      return '';
  }
};

/**
 * ドロップダウンの表示状態を切り替えます。
 */
const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

/**
 * オプションを選択し、親コンポーネントに選択値を通知します。
 * @param item - 選択したアイテム
 */
const selectOption = (item: DownloadMenuItems) => {
  emit('selectedItem', item);
  isOpen.value = false;
  emit('close');
};

/**
 * ドロップダウンを閉じます。
 * @param event - クリックイベント
 */
const closeDropdown = (event: MouseEvent) => {
  if (!isOpen.value) return;
  const target = event.target;
  const selector = '#download-menu-dropdown-container';
  const arrowSelector = '#download-menu-dropdown-arrow';
  const searchSelector = '#download-menu-dropdown-content';
  if (!(target instanceof Element)) return;
  if (target.closest(arrowSelector)) return;
  if (target.closest(selector)) return;
  if (target.closest(searchSelector)) return;

  isOpen.value = false;
  emit('close');
};

onMounted(() => {
  document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
  document.removeEventListener('click', closeDropdown);
});

/**
 * ドロップダウンの位置を計算し、設定します。
 */
const calculateDropdownPosition = () => {
  nextTick(() => {
    const selectBox = document.querySelector('#download-menu-dropdown-container') as HTMLElement;
    if (selectBox) {
      const rect = selectBox.getBoundingClientRect();
      const dropdown = document.querySelector('#download-menu-dropdown-content') as HTMLElement;
      if (dropdown) {
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.top = `${rect.bottom}px`;

        // ドロップダウンの高さを計算
        const desiredHeight = OPTION_HEIGHT * DOWNLOAD_MENU_ITEMS.length;

        // max-heightを計算して設定
        const availableHeight = window.innerHeight - rect.bottom - 10; // 10pxは少しのマージン
        dropdown.style.maxHeight = `${Math.min(desiredHeight, availableHeight)}px`;
      }
    }
  });
};
</script>

<template>
  <div class="download-menu_container" @click="closeDropdown($event)">
    <div id="download-menu-dropdown-container" class="download-menu">
      <div class="download-menu__selected" @click="toggleDropdown">
        <SvgLoader id="download-menu-dropdown-arrow" name="download" class="download-menu__icon" />
        <div class="download-menu__field">
          {{ t('ダウンロード') }}
        </div>
        <SvgLoader v-if="isOpen" name="arrowUpWhite" class="download-menu__arrow" />
        <SvgLoader v-else name="arrowDownWhite" class="download-menu__arrow" />
      </div>
      <Teleport v-if="isOpen" to="body">
        <div id="download-menu-dropdown-content" class="download-menu__dropdown">
          <div v-for="option in DOWNLOAD_MENU_ITEMS" :key="option" class="download-menu__option" @click="selectOption(option)">
            {{ getDisplayText(option) }}
          </div>
        </div>
      </Teleport>
    </div>
  </div>
</template>

<style scoped lang="scss">
.download-menu {
  justify-items: center;
  position: relative;
  user-select: none;
  box-sizing: border-box;
  font-family: Noto Sans JP;

  &__container {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    gap: 0;
  }

  &__field {
    height: 20px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0em;
    color: $semanticWhite;
  }

  &__selected {
    width: 100%;
    min-height: 32px;
    padding: 6px 16px;
    display: grid;
    grid-template-columns: 22px 1fr 24px;
    text-align: center;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    background-color: $semanticPrimary;
    border: 1px solid $semanticPrimary;
    border-radius: 4px;
  }

  &__icon {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 22px;
    width: 22px;
  }

  &__arrow {
    display: grid;
    align-content: center;
    justify-content: center;
    height: 24px;
    width: 24px;
  }

  &__dropdown {
    position: fixed;
    background-color: $semanticWhite;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    z-index: 1050;
    overflow-y: auto;
    max-height: 300px;
    box-sizing: border-box;
  }

  &__option {
    padding: 10px;
    cursor: pointer;

    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0em;
    text-align: left;
    color: $textDefault;

    min-height: 32px;

    &:hover,
    &:focus {
      background-color: $semanticPrimarySubdued;
    }
  }
}
</style>
